import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { BadgeCheck, ThumbsUp } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Field } from "@/components/forms";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateRecommendation, usePartnerStudents } from "@/hooks/usePartnerData";

import { StudentPicker } from "@/components/shared/StudentPicker";

export const Route = createFileRoute("/partner/recommendations/new")({
  component: NewRecommendation,
});

type PartnerStudent = {
  id: string | number;
  name: string;
  status?: string;
};

function NewRecommendation() {
  const [student, setStudent] = useState("");
  const [status, setStatus] = useState("");
  const [comments, setComments] = useState("");
  const [done, setDone] = useState(false);
  const { data: studentsData = [] } = usePartnerStudents();
  const createRecommendation = useCreateRecommendation();

  const selectedStudent = studentsData.find(
    (s: PartnerStudent) => String(s.id) === String(student),
  );

  const handleSubmit = async () => {
    if (student && status && comments) {
      try {
        await createRecommendation.mutateAsync({
          student_id: Number(student),
          content: `[${status}] ${comments}`,
        });
        setDone(true);
      } catch (err) {
        console.error("Recommendation submission failed:", err);
      }
    }
  };

  if (done) {
    const studentName = selectedStudent?.name || "Student";
    return (
      <div className="space-y-6">
        <PageHeader
          title="Endorsement confirmed"
          subtitle="Your professional reference has been securely recorded in the platform ledger."
          breadcrumbs={[
            { label: "Recommendations", path: "/partner/recommendations" },
            { label: "New" },
          ]}
        />
        <div className="mx-auto max-w-2xl">
          <SectionCard title="Submission status">
            <div className="flex flex-col items-center py-4 text-center">
              <div className="mb-4 flex size-10 items-center justify-center rounded-md border border-success/30 bg-success/10 text-success">
                <BadgeCheck className="size-5" aria-hidden />
              </div>
              <h2 className="text-title text-foreground">Submission successful</h2>
              <p className="mt-1 max-w-md text-sm leading-6 text-muted-foreground">
                Professional endorsement for{" "}
                <span className="font-medium text-foreground">{studentName}</span> has been
                transmitted to institutional supervisors.
              </p>

              <dl className="mt-6 w-full space-y-4 rounded-md border border-border bg-muted/40 p-4 text-left">
                <div>
                  <dt className="eyebrow mb-1.5">Student</dt>
                  <dd className="flex items-center gap-2">
                    <Avatar className="size-7 rounded-full">
                      <AvatarFallback className="rounded-full text-xs">
                        {studentName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-foreground">{studentName}</span>
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow mb-1.5">Evaluation</dt>
                  <dd>
                    <Badge variant="success" dot>
                      {status === "highly" ? "Highly Recommended" : "Suitable for Employment"}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow mb-1.5">Comments</dt>
                  <dd className="rounded-sm border border-border bg-card p-3 text-sm leading-6 text-body">
                    "{comments}"
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex w-full flex-col-reverse justify-center gap-2 sm:flex-row">
                <Button
                  onClick={() => {
                    setDone(false);
                    setStudent("");
                    setStatus("");
                    setComments("");
                  }}
                  variant="outline"
                >
                  Draft another
                </Button>
                <Button asChild>
                  <Link to="/partner/recommendations">Return to ledger</Link>
                </Button>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Professional endorsement"
        subtitle="Calibrate a student's professional trajectory with performance-based references."
        breadcrumbs={[
          { label: "Recommendations", path: "/partner/recommendations" },
          { label: "New" },
        ]}
      />

      <div className="mx-auto max-w-2xl">
        <SectionCard
          title="Assessment criteria"
          description="Define the student's performance benchmarks and professional suitability."
        >
          <div className="space-y-5">
            <Field label="Select active student" required>
              {({ id }) => (
                <StudentPicker
                  id={id}
                  value={student || null}
                  onChange={(v) => setStudent(v ?? "")}
                  placeholder="Choose a student"
                  subtitle="Only students active in your organisation are listed."
                  options={studentsData
                    .filter((s: PartnerStudent) => s.status === "active")
                    .map((s: PartnerStudent) => ({ value: String(s.id), label: s.name }))}
                />
              )}
            </Field>

            <Field label="Recommendation grade" required>
              {({ id }) => (
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger id={id}>
                    <SelectValue placeholder="Choose a grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="highly">Highly Recommended (Top 10%)</SelectItem>
                    <SelectItem value="suitable">Suitable for Industrial Employment</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Qualitative observations" required>
              <Textarea
                placeholder="Provide detailed feedback regarding technical proficiency, work ethic, and cultural fit..."
                rows={5}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </Field>

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
              <Button variant="outline" asChild>
                <Link to="/partner/recommendations">Cancel</Link>
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!student || !status || !comments}
                loading={createRecommendation.isPending}
              >
                {!createRecommendation.isPending && <ThumbsUp />}
                {createRecommendation.isPending ? "Submitting..." : "Finalize endorsement"}
              </Button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
